import React, { useEffect, useRef, useState } from "react";
import { Share2, Copy, FileText, Download, Captions, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useA11y } from "@/context/A11yContext";
import { useAuth } from "@/context/AuthContext";
import { PROMO_TRANSCRIPTS, PROMO_SOCIAL } from "@/data/promoVideos";

const P = "/media/promo";
const V = "v=20261103";
const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
// Phones and slow connections get a lighter 360p copy so sound doesn't stutter while the video buffers.
const lightVideo = () => {
  const c = navigator.connection || {};
  return window.matchMedia?.("(max-width: 767px)").matches || c.saveData || ["slow-2g", "2g", "3g"].includes(c.effectiveType);
};

const SoundButton = ({ videoRef, onDone }) => (
  <button type="button" data-testid="course-highlight-sound-btn"
    onClick={() => { const v = videoRef.current; if (!v) return; v.muted = false; v.loop = false; v.currentTime = 0; v.play().catch(() => {}); onDone(); }}
    className="absolute bottom-16 left-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-105">
    <Volume2 size={16} /> Tap for sound
  </button>
);

export const CourseHighlight = ({ instrumentId, courseId, title, videoKey, path, heading = "Watch the highlights", autoPlay = false }) => {
  const key = videoKey || `${instrumentId}-${courseId === "gospel-beginner" ? "gospel" : courseId}`;
  const transcript = PROMO_TRANSCRIPTS[key];
  const { captions } = useA11y();
  const { user } = useAuth();
  const ref = useRef(null);
  const [showT, setShowT] = useState(false);
  const [auto] = useState(() => autoPlay && !reducedMotion());
  const [muted, setMuted] = useState(auto);
  const [light] = useState(lightVideo);
  useEffect(() => {
    const tr = ref.current?.textTracks?.[0];
    if (tr) tr.mode = captions ? "showing" : "hidden";
  }, [captions, key]);
  if (!transcript) return null;
  const url = `${window.location.origin}${path || `/courses/${instrumentId}/${courseId}`}`;
  const text = `${title} with Jason Thompson — The JT Hub`;
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: text, text, url }); return; } catch (e) { return; } }
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied — paste it anywhere to share"));
  };
  const enc = encodeURIComponent;
  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-border bg-card" data-testid="course-highlight">
      <div className="relative">
        <video key={key} ref={ref} controls playsInline preload={auto ? "auto" : "metadata"} poster={`${P}/${key}-wide.jpg?${V}`} className="aspect-video w-full bg-black"
          autoPlay={auto} muted={auto} loop={auto} onVolumeChange={(e) => { if (!e.target.muted) setMuted(false); }}
          aria-label={`Highlights video: ${title}`} data-testid="course-highlight-video">
          <source src={`${P}/${key}-${light ? "mobile" : "wide"}.mp4?${V}`} type="video/mp4" />
          <track kind="captions" srcLang="en" label="English" src={`${P}/${key}.vtt?${V}`} default={captions} />
        </video>
        {muted && <SoundButton videoRef={ref} onDone={() => setMuted(false)} />}
      </div>
      <div className="flex flex-wrap items-center gap-2 p-4">
        <p className="mr-auto font-serif text-lg">{heading} <span className="text-sm text-muted-foreground">· {key === "showreel" ? "just over 2 minutes" : "under 2 minutes"}</span></p>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setShowT((s) => !s)} aria-expanded={showT} data-testid="course-highlight-transcript-toggle"><FileText size={14} className="mr-1.5" /> Transcript</Button>
        <Button size="sm" className="rounded-full" onClick={share} data-testid="course-highlight-share"><Share2 size={14} className="mr-1.5" /> Share</Button>
        <a href={`https://wa.me/?text=${enc(`${text} ${url}`)}`} target="_blank" rel="noreferrer" data-testid="course-highlight-whatsapp"><Button size="sm" variant="outline" className="rounded-full">WhatsApp</Button></a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="course-highlight-facebook"><Button size="sm" variant="outline" className="rounded-full">Facebook</Button></a>
        <a href={`https://x.com/intent/post?text=${enc(text)}&url=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="course-highlight-x"><Button size="sm" variant="outline" className="rounded-full">X</Button></a>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"))} data-testid="course-highlight-copy" aria-label="Copy link"><Copy size={14} /></Button>
        {user?.role === "admin" && (
          <>
            <a href={`${P}/${key}-vertical.mp4?${V}`} download data-testid="course-highlight-download-vertical"><Button size="sm" variant="outline" className="rounded-full"><Download size={14} className="mr-1.5" /> Reels / TikTok (9:16)</Button></a>
            <a href={`${P}/${key}-wide.mp4?${V}`} download data-testid="course-highlight-download-wide"><Button size="sm" variant="outline" className="rounded-full"><Download size={14} className="mr-1.5" /> 16:9</Button></a>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="course-highlight-copy-post"
              onClick={() => navigator.clipboard.writeText(`${PROMO_SOCIAL[key]}\n${url}`).then(() => toast.success("Post caption + hashtags copied"))}><Copy size={14} className="mr-1.5" /> Copy post caption</Button>
          </>
        )}
      </div>
      {showT && (
        <div className="border-t border-border p-5" data-testid="course-highlight-transcript">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Captions size={14} /> Transcript</p>
          <p className="mt-2 leading-relaxed text-muted-foreground">{transcript}</p>
        </div>
      )}
    </section>
  );
};
